/**
 * web/src/api/types.ts
 * Shared wire types, hand-maintained to mirror the FastAPI/SQLModel backend
 * (the Node→Python migration retired Prisma; `prisma/` is reference-only).
 *
 * Field-name source of truth: src/pixel/models.py (and the alembic schema).
 * Every API route returns a model row (or a subset), so these interfaces MUST
 * match the model field names. JSON columns deserialize to
 * `Record<string, unknown>` (or a discriminated union for gateData where the
 * Phase 6 contract is known); datetime columns arrive as ISO strings.
 */

/** From src/auth/types.ts. */
export type Role = 'sales_ops' | 'content' | 'operations' | 'admin';

/**
 * From prisma/schema.prisma Workflow.status. Note: schema.prisma is loosely
 * typed (`String`) — these are the values the engine actually writes,
 * matching src/api/workflows.ts ListQuery.
 */
export type WorkflowStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** From prisma/schema.prisma StepExecution.status. */
export type StepStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'waiting_approval'
  | 'waiting_event'
  | 'rejected'
  | 'scheduled'
  | 'skipped';

/** From prisma/schema.prisma StepExecution.stepType. */
export type StepType = 'automated' | 'human_gate';

/** From prisma/schema.prisma TrackedEmail.status. */
/** CLOSED = cleared by "close out event" after the event ended (no longer awaited). */
export type TrackedEmailStatus =
  | 'PENDING'
  | 'IN_CONVERSATION'
  | 'CONFIRMED'
  | 'NEEDS_ESCALATION'
  | 'CLOSED';

/** From prisma/schema.prisma Event.format. */
export type EventFormat = 'festival' | 'retreat' | 'citywide';

/** Embedded event projection used in list/detail responses. */
export interface EventRef {
  id: string;
  code: string;
  name: string;
  hubspotPortalId?: string | null;
}

/** Mirror of prisma/schema.prisma model Workflow. */
export interface Workflow {
  id: string;
  eventId: string;
  type: string;
  status: WorkflowStatus;
  displayName: string;
  triggerSource: string;
  context: Record<string, unknown>;
  currentStepIndex: number;
  /**
   * Status of the StepExecution at currentStepIndex. The workflow's top-level
   * `status` is just 'paused' for every paused situation (gate awaiting
   * approval, gate rejected, or an automated step waiting on an external
   * event), so the list/detail badge reads this to distinguish them. Absent on
   * older cached rows / freshly triggered workflows — treat undefined as unknown.
   */
  currentStepStatus?: StepStatus | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  /**
   * Soft-hide flag. Archived workflows are excluded from the default list +
   * stat counts; the dashboard only fetches them with the "Show archived"
   * toggle on, and renders them dimmed with an Unarchive action. Absent on
   * older cached rows — treat undefined as not archived.
   */
  isArchived?: boolean;
  /**
   * Optimistic UI hint populated by Phase 5 WS handler from `step_failed` /
   * `workflow_failed` event payloads (`details.failureReason`). NOT a Prisma
   * column today — the engine surfaces failure context via AuditEntry.details
   * — but the dashboard's hybrid cache update (D-2.1, plan 05-03 W-1 fix) lifts
   * the WS payload's failureReason into the cached Workflow row so list cards
   * can render the failure copy without a Detail-page round-trip. Wire shape
   * does not include this field; treat as undefined when absent.
   */
  failureReason?: string | null;
  /** Included by GET /api/workflows + /api/workflows/:id (event: { id, code, name }). */
  event?: EventRef;
}

/**
 * Discriminated union for `StepExecution.gateData` JSON. Phase 6 sets the
 * `kind` discriminator; Phase 5 components branch on it for FormRenderer
 * vs. EmailPreview. Wire shape is `Record<string, unknown> | null` until
 * Phase 6 — Phase 5 narrows via a runtime check on `kind`.
 */
export interface GateDataDataKind {
  kind: 'data-gate';
  /** JSON Schema-shaped object describing fields. */
  schema: Record<string, unknown>;
  defaults?: Record<string, unknown>;
  readonly?: string[];
  lastEdits?: Record<string, unknown>;
  rejectionReason?: string;
  /**
   * Before/after diff of the agent's Sponsorship Tracker write (step 2),
   * surfaced on the supplementary-review gate with a Revert action.
   * Null/absent when the step stored no pre-image snapshot (legacy
   * workflows, snapshot read failure).
   */
  trackerChanges?: TrackerChanges | null;
}

/** One Tracker column the agent wrote: live header, display label, before/after. */
export interface TrackerChangeColumn {
  header: string;
  /** Header with embedded newlines flattened for display. */
  label: string;
  before: unknown;
  after: unknown;
}

/**
 * The agent's Tracker write at a revertable step, as captured by its
 * pre-image snapshot. `revertedAt`/`revertedBy` here are the values at
 * gate-build time — read the live workflow.context slice for post-revert
 * freshness (the gateData blob is static once the gate opens).
 */
export interface TrackerChanges {
  stepId: string;
  rowNumber: number;
  /** False when the agent appended a new row (revert blanks the cells). */
  rowExisted: boolean;
  revertedAt?: string | null;
  revertedBy?: string | null;
  columns: TrackerChangeColumn[];
}
export interface GateDataEmailKind {
  kind: 'email-gate';
  email: { to: string; cc?: string; bcc?: string; subject: string; body: string };
  context?: Record<string, unknown>;
  lastEdits?: Record<string, unknown>;
  rejectionReason?: string;
  /**
   * Speaker promotion gate only: the card-candidate manifest for the QC
   * panel. Loosely typed on the wire — read via `readCardCandidates`.
   */
  cardCandidates?: unknown;
}
export type GateData = GateDataDataKind | GateDataEmailKind;

/**
 * Narrows the widened `GateData | Record<string, unknown> | null` wire shape
 * (StepExecution.gateData) to the discriminated `GateData` union by checking
 * for a known `kind`. Lets consumers switch on `gd.kind` with full inference
 * instead of re-casting `(gd as { kind?: string })` at each access (F22).
 *
 * @param x a gateData value (possibly legacy pre-Phase-6 JSON or null)
 * @returns true when `x` is a tagged GateData variant
 */
export function isGateData(x: unknown): x is GateData {
  return (
    typeof x === 'object' &&
    x !== null &&
    ((x as { kind?: unknown }).kind === 'data-gate' ||
      (x as { kind?: unknown }).kind === 'email-gate')
  );
}

/* -------------------------------------------------------------------------- */
/* Ops next-steps template picker (step_08 email-gate context)                */
/* -------------------------------------------------------------------------- */

/**
 * One pickable section parsed from an ops next-steps Doc — a single booth type
 * or deliverable. `subject` is the section's own email subject when the
 * ingestor found one, else null. The verbatim body HTML is intentionally NOT
 * on the wire shape: the server composes the merged body, so the picker only
 * needs labels. Shared by the gate picker and the EventConfigure status view.
 */
export interface OpsTemplateSection {
  sectionKey: string;
  label: string;
  subject: string | null;
}

/** A group of sections from one source Doc (Exhibit/Booth vs. Deliverables). */
export interface OpsTemplateGroup {
  docKey: string;
  label: string;
  /** Ingest status of the backing Doc: 'ok' | 'review' | 'stale' | 'error'. */
  status: string;
  sectionCount: number;
  sections: OpsTemplateSection[];
}

/**
 * The ops gate's section-picker state, carried on
 * `gateData.context.templatePicker` and echoed by the regenerate endpoint.
 * Drives the single-select section picker in GateReview. Post-2026-06: every
 * variant (booth footprints + "no exhibit space" cases) ships as a section
 * of one Doc, so the picker is one group with a radio-style single pick —
 * no deliverables group, no holding-email toggle.
 */
export interface OpsTemplatePicker {
  groups: OpsTemplateGroup[];
  /** sectionKey that produced the gate's current body (list-of-one). */
  selected: string[];
  /** Human label for the current selection (list-of-one). */
  selectedLabels: string[];
  /** Claude's auto-suggested sectionKeys (the default selection on open). */
  suggested: string[];
  suggestReasoning: string;
  suggestConfidence: string;
  /** True when no template sources are stored yet (admin must Sync first). */
  storeEmpty: boolean;
}

/**
 * Reads the ops `templatePicker` block out of an email-gate context (or the
 * regenerate response's `context`). Returns null when absent or malformed, so
 * non-ops email gates render unchanged. Mirrors `readCopyRationale`'s defensive
 * shape-check style — the wire type is `Record<string, unknown>`.
 *
 * @param context an email-gate `gateData.context` (or regenerate `context`)
 * @returns the typed picker, or null when this gate has no section picker
 */
export function readTemplatePicker(
  context: Record<string, unknown> | undefined | null,
): OpsTemplatePicker | null {
  if (!context || typeof context !== 'object') return null;
  const raw = (context as { templatePicker?: unknown }).templatePicker;
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Partial<OpsTemplatePicker>;
  if (!Array.isArray(p.groups)) return null;
  return {
    groups: p.groups,
    selected: Array.isArray(p.selected) ? p.selected : [],
    selectedLabels: Array.isArray(p.selectedLabels) ? p.selectedLabels : [],
    suggested: Array.isArray(p.suggested) ? p.suggested : [],
    suggestReasoning: typeof p.suggestReasoning === 'string' ? p.suggestReasoning : '',
    suggestConfidence: typeof p.suggestConfidence === 'string' ? p.suggestConfidence : '',
    storeEmpty: p.storeEmpty === true,
  };
}

/* -------------------------------------------------------------------------- */
/* Speaker-card candidates (promotion email-gate QC leg)                       */
/* -------------------------------------------------------------------------- */

/**
 * One generated speaker-card portrait candidate in the promotion gate's
 * manifest. `index` is 1-based and doubles as the image-proxy path segment
 * (`GET /api/workflows/{workflowId}/card-candidates/{index}`); the Drive
 * fields identify the stored PNG server-side.
 */
export interface CardCandidate {
  index: number;
  driveFileId: string;
  fileName: string;
  webViewLink: string;
}

/**
 * The promotion gate's card-candidate manifest, carried at
 * `gateData.cardCandidates` (server: card_pipeline.generate_card_candidates).
 * `model` is the imagegen model label (present on `ok`); `error` is the
 * failure summary (present on `error`). All non-ok statuses carry an empty
 * `candidates` list, leaving only the "skip" choice. `not_generated` is the
 * cutover gate's deliberate empty state (card_gate.STATUS_NOT_GENERATED) —
 * generation is user-initiated there via the Regenerate action.
 *
 * `reuseStage` is set when NOTHING was generated because the monday row
 * already carried artwork (server: card_pipeline.reuse_monday_deliverables) —
 * `card` means there is nothing left to make at all, `final` means the
 * finished cut-out was picked up (so there is no halo left to paint), `v1`
 * means a plain draft was picked up and still needs a color. `existing` maps
 * each occupied board column title to the file names found in it.
 */
export interface CardCandidateManifest {
  status: 'ok' | 'no_headshot' | 'not_generated' | 'error';
  model?: string;
  error?: string;
  candidates: CardCandidate[];
  reuseStage?: 'card' | 'final' | 'v1';
  existing?: Record<string, string[]>;
}

/**
 * Reads the `cardCandidates` manifest out of a promotion gate's gateData.
 * Returns null when absent or malformed, so every other email gate renders
 * unchanged. Mirrors `readTemplatePicker`'s defensive shape-check style —
 * the wire type is `Record<string, unknown>`; candidate entries without a
 * numeric `index` are dropped (the index is both the selection value and
 * the image URL segment).
 *
 * @param gateData a step's gateData value (possibly legacy or null)
 * @returns the typed manifest, or null when this gate has no card leg
 */
export function readCardCandidates(gateData: unknown): CardCandidateManifest | null {
  if (!gateData || typeof gateData !== 'object') return null;
  const raw = (gateData as { cardCandidates?: unknown }).cardCandidates;
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as {
    status?: unknown;
    model?: unknown;
    error?: unknown;
    candidates?: unknown;
    reuseStage?: unknown;
    existing?: unknown;
  };
  if (
    m.status !== 'ok' &&
    m.status !== 'no_headshot' &&
    m.status !== 'not_generated' &&
    m.status !== 'error'
  ) {
    return null;
  }
  const candidates: CardCandidate[] = Array.isArray(m.candidates)
    ? (m.candidates as unknown[]).flatMap((c) => {
        if (!c || typeof c !== 'object') return [];
        const cc = c as {
          index?: unknown;
          driveFileId?: unknown;
          fileName?: unknown;
          webViewLink?: unknown;
        };
        if (typeof cc.index !== 'number') return [];
        return [
          {
            index: cc.index,
            driveFileId: typeof cc.driveFileId === 'string' ? cc.driveFileId : '',
            fileName: typeof cc.fileName === 'string' ? cc.fileName : '',
            webViewLink: typeof cc.webViewLink === 'string' ? cc.webViewLink : '',
          },
        ];
      })
    : [];
  // Occupied board columns → their file names. Unknown shapes are dropped
  // rather than guessed: the notice text names the columns, so a bad entry
  // would put a lie on screen.
  const existing: Record<string, string[]> = {};
  if (m.existing && typeof m.existing === 'object') {
    for (const [column, names] of Object.entries(m.existing as Record<string, unknown>)) {
      if (Array.isArray(names)) {
        existing[column] = names.filter((n): n is string => typeof n === 'string');
      }
    }
  }
  const reuseStage =
    m.reuseStage === 'card' || m.reuseStage === 'final' || m.reuseStage === 'v1'
      ? m.reuseStage
      : undefined;
  return {
    status: m.status,
    ...(typeof m.model === 'string' ? { model: m.model } : {}),
    ...(typeof m.error === 'string' ? { error: m.error } : {}),
    candidates,
    ...(reuseStage ? { reuseStage } : {}),
    ...(Object.keys(existing).length > 0 ? { existing } : {}),
  };
}

/** One highlight-halo swatch from the card gate's palette. */
export interface HighlightColor {
  name: string;
  hex: string;
}

/**
 * The finalized colored-halo preview, carried at `gateData.finalPreview` when
 * the card gate is in its `final` phase. The image itself is served by the
 * candidate proxy at index `"final"`; `chosenCandidate` names the draft the
 * final was painted from (approval requires the pick to still match).
 */
export interface CardFinalPreview {
  driveFileId: string;
  fileName: string;
  webViewLink: string;
  color: HighlightColor;
  chosenCandidate: string;
  /**
   * `"final"` when this preview IS the board's existing finished cut-out
   * rather than something we painted. There is no draft behind it and no halo
   * to re-color, so the panel hides the re-color/back controls (the server
   * rejects a finalize on it).
   */
  reuseStage?: 'final';
}

/**
 * The Speaker Card staged in Drive for review, carried at
 * `gateData.cardPreview` when the card gate is in its `card` phase. Served by
 * the candidate proxy at index `"card"`; approval republishes these exact
 * bytes. `status: 'unavailable'` means the server's rasterizer is degraded so
 * there is no image to show — the phase still advances (approval publishes the
 * illustration deliverables) and `driveFileId` is empty.
 */
export interface StagedCardPreview {
  status: 'ok' | 'unavailable';
  driveFileId: string;
  fileName: string;
  webViewLink: string;
  /** The published card's file name (`{Name} - Speaker Card.png`). */
  deliverableName: string;
  /** The draft the card was built from — approval requires the pick to match. */
  chosenCandidate: string;
}

/**
 * The card gate's three-phase state, read off the data-gate `defaults` blob
 * (server: card_gate._card_payload) — the reviewer's three artefacts:
 * `draft` (illustration proposals) → `final` (the finished illustration, once
 * a finalize lands a `finalPreview`) → `card` (the speaker card itself, once a
 * build lands a `cardPreview`). `highlightColors` are the event's swatches and
 * `highlightColor` the seeded choice (a palette name or `"random"`).
 */
export interface CardGateState {
  phase: 'draft' | 'final' | 'card';
  highlightColors: HighlightColor[];
  highlightColor: string;
  finalPreview: CardFinalPreview | null;
  cardPreview: StagedCardPreview | null;
}

/**
 * Reads the three-phase card-gate state out of the gate's defaults blob.
 * Defensive like `readCardCandidates`: a missing/malformed blob yields the
 * draft phase with an empty palette, so pre-rework gate rows (or non-card
 * gates probed by mistake) degrade to the old single-phase behavior. Each
 * phase requires its own artefact to have parsed, so a gate persisted at
 * `card` with a dropped preview renders as `final` rather than blank.
 *
 * @param gateData a card gate's `gateData.defaults` value
 * @returns the typed phase state (never null — draft is the safe floor)
 */
export function readCardGateState(gateData: unknown): CardGateState {
  const g = (gateData ?? {}) as {
    cardPhase?: unknown;
    highlightColors?: unknown;
    highlightColor?: unknown;
    finalPreview?: unknown;
    cardPreview?: unknown;
  };
  const highlightColors: HighlightColor[] = Array.isArray(g.highlightColors)
    ? (g.highlightColors as unknown[]).flatMap((c) => {
        const cc = (c ?? {}) as { name?: unknown; hex?: unknown };
        return typeof cc.name === 'string' && typeof cc.hex === 'string'
          ? [{ name: cc.name, hex: cc.hex }]
          : [];
      })
    : [];
  let finalPreview: CardFinalPreview | null = null;
  if (g.finalPreview && typeof g.finalPreview === 'object') {
    const p = g.finalPreview as {
      driveFileId?: unknown;
      fileName?: unknown;
      webViewLink?: unknown;
      color?: unknown;
      chosenCandidate?: unknown;
      reuseStage?: unknown;
    };
    const color = (p.color ?? {}) as { name?: unknown; hex?: unknown };
    if (typeof p.driveFileId === 'string' && p.driveFileId) {
      finalPreview = {
        driveFileId: p.driveFileId,
        fileName: typeof p.fileName === 'string' ? p.fileName : '',
        webViewLink: typeof p.webViewLink === 'string' ? p.webViewLink : '',
        color: {
          name: typeof color.name === 'string' ? color.name : '',
          hex: typeof color.hex === 'string' ? color.hex : '',
        },
        chosenCandidate: typeof p.chosenCandidate === 'string' ? p.chosenCandidate : '',
        ...(p.reuseStage === 'final' ? { reuseStage: 'final' as const } : {}),
      };
    }
  }
  // The staged card. An 'unavailable' status is kept (the panel explains why
  // there's no image); anything else needs a real Drive id to be renderable.
  let cardPreview: StagedCardPreview | null = null;
  if (g.cardPreview && typeof g.cardPreview === 'object') {
    const p = g.cardPreview as {
      status?: unknown;
      driveFileId?: unknown;
      fileName?: unknown;
      webViewLink?: unknown;
      deliverableName?: unknown;
      chosenCandidate?: unknown;
    };
    const driveFileId = typeof p.driveFileId === 'string' ? p.driveFileId : '';
    const unavailable = p.status === 'unavailable';
    if (unavailable || driveFileId) {
      cardPreview = {
        status: unavailable ? 'unavailable' : 'ok',
        driveFileId,
        fileName: typeof p.fileName === 'string' ? p.fileName : '',
        webViewLink: typeof p.webViewLink === 'string' ? p.webViewLink : '',
        deliverableName: typeof p.deliverableName === 'string' ? p.deliverableName : '',
        chosenCandidate: typeof p.chosenCandidate === 'string' ? p.chosenCandidate : '',
      };
    }
  }
  const phase =
    g.cardPhase === 'card' && finalPreview != null && cardPreview != null
      ? 'card'
      : (g.cardPhase === 'final' || g.cardPhase === 'card') && finalPreview != null
        ? 'final'
        : 'draft';
  return {
    phase,
    highlightColors,
    highlightColor: typeof g.highlightColor === 'string' ? g.highlightColor : 'random',
    finalPreview,
    cardPreview,
  };
}

/**
 * Mirror of prisma/schema.prisma model StepExecution. Note the schema's
 * approval columns: approvedBy (user id, nullable string) and
 * approvedByName (display name) — NOT approvedById. approverEdits (JSON)
 * holds the form payload from the most recent approval. Rejection has
 * only rejectionReason — there is no rejectedById column.
 */
export interface StepExecution {
  id: string;
  workflowId: string;
  stepIndex: number;
  stepId: string;
  stepName: string;
  stepType: StepType;
  status: StepStatus;

  // Gate fields
  gateRole: Role | null;
  gateSchema: Record<string, unknown> | null;
  gateData: GateData | Record<string, unknown> | null;

  // Approval tracking
  approvedBy: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  approverEdits: Record<string, unknown> | null;
  rejectionReason: string | null;

  output: Record<string, unknown> | null;
  errorMessage: string | null;
  retryCount: number;

  /** Phase 2 ENG-07 — engine withProgress() sub-effect dedup map. Always an object. */
  progress: Record<string, unknown>;

  // Scheduling
  scheduledFor: string | null;
  sendWindowStart: string | null;
  sendWindowEnd: string | null;

  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;

  createdAt: string;
  updatedAt: string;
}

/**
 * Mirror of prisma/schema.prisma model TrackedEmail. Schema uses
 * `recipientEmail` / `senderEmail` / `stepId` (NOT `recipient` / `stepIndex`).
 * `confirmedBy` is a string (user id OR a sentinel like "auto:llm" /
 * "auto:hubspot") — NOT a separate `confirmedById` column.
 */
export interface TrackedEmail {
  id: string;
  workflowId: string;
  stepId: string;

  externalId: string | null;
  threadId: string | null;

  recipientEmail: string;
  recipientName: string | null;
  senderEmail: string;
  subject: string;

  status: TrackedEmailStatus;

  lastClassification: Record<string, unknown> | null;
  classificationHistory: unknown[];

  followUpDueAt: string | null;
  followUpSentAt: string | null;
  followUpCount: number;

  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  repliedAt: string | null;

  confirmedAt: string | null;
  confirmedBy: string | null;
  confirmationMethod: string | null;

  /** P2 nudge substrate: the follow-up schedule attached to this sent email. */
  nudgePlan: NudgePlanEntry[];
  /** Which composer renders the nudge body ('speaker_invite' | 'speaker_asset'); null = no plan. */
  nudgeTemplate: string | null;

  createdAt: string;
  updatedAt: string;

  /** Included by GET /api/tracked-emails (workflow: { id, displayName, eventId, type }). */
  workflow?: Pick<Workflow, 'id' | 'displayName' | 'eventId' | 'type'>;
}

/**
 * One entry in a TrackedEmail's nudge plan (P2 nudge substrate — server:
 * pixel.email.nudges entry shape). `to`/`cc` of null mean "inherit the
 * original email's recipients at fire time". `sent` entries are immutable —
 * the PATCH route 409s if one is altered or dropped.
 */
export interface NudgePlanEntry {
  /** ISO datetime (naive UTC) when this nudge is due to fire. */
  dueAt: string;
  to: string[] | null;
  cc: string[] | null;
  status: 'planned' | 'sent' | 'cancelled';
  sentAt: string | null;
  /** When the scheduler pinged Slack that this entry is due (2026-07
   *  notify-not-send: nudges never auto-fire — a human sends from here).
   *  Dedup stamp: each entry is prompted exactly once. */
  notifiedAt?: string | null;
  /** Fire-time stamp of what a sent bump chased ("Chased 2: Speaker bio,
   *  Registration") — display-only history on sent rows. */
  sentSummary?: string | null;
  /** Last send failure on a still-planned entry (surfaced for the human to
   *  retry — the scheduler never sends). */
  error: string | null;
  /** Why a cancelled entry was cancelled (confirmed / reply_detected / ...). */
  cancelReason: string | null;
  /** Inbound-activity evidence when cancelReason is `inbound_activity` — who
   *  wrote to the sender, when, and whether it was on this email's thread. */
  cancelDetail?: {
    from?: string;
    at?: string;
    inThread?: boolean;
    subject?: string;
  } | null;
}

/**
 * Mirror of prisma/schema.prisma model AuditEntry. Schema uses `details`
 * (Json?, optional) — NOT `payload`. There is no `actorType` column;
 * `actorId` is a string that may be a user id OR the literal "system".
 * `stepId` is the audit-time step id (string), NOT a `stepIndex` integer.
 */
export interface AuditEntry {
  id: string;
  workflowId: string;
  action: string;
  stepId: string | null;
  actorId: string | null;
  actorName: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

/**
 * Per-event module toggles — stored at `integrationConfig.modules`. Controls
 * which platform areas are visible for this event. Every key defaults to `true`
 * and the entire `modules` object may be absent on existing events — always
 * treat a missing key as enabled. These are cosmetic gates in the UI; the
 * backend 404s the underlying routes regardless.
 */
export interface EventModules {
  /** Sponsor onboarding pages (Sponsors, Approvals). Default true. */
  sponsor?: boolean;
  /** Speaker onboarding pages (Speakers, Approvals). Default true. */
  speaker?: boolean;
  /** Accommodation pages (Pickup report, Email agent). Default true. */
  accommodation?: boolean;
  /** Sales proposal pages. Default true. */
  sales?: boolean;
}

/** Mirror of prisma/schema.prisma model Event. */
export interface Event {
  id: string;
  name: string;
  code: string;
  format: EventFormat;
  startDate: string;
  endDate: string;
  /** The event's final day has passed: Slack pings stop (except speaker
   *  thank-yous) and "close out event" unlocks. Server-computed. */
  isOver?: boolean;
  inProgressDate: string | null;
  optInDate: string | null;
  closedWonStageId: string | null;
  /** Extra HubSpot dealstage IDs (beyond closedWonStageId) that also start
   *  onboarding. The effective trigger set is the union of the two; a deal
   *  moving between two stages already in the set does not re-trigger. */
  closedWonStageIds: string[];
  /** Brand this event belongs to (credential-isolation boundary). Null = the
   *  default brand (resolves the unsuffixed global env vars). */
  brandId: string | null;
  hubspotPortalId: string | null;
  sheetId: string | null;
  driveRootFolderId: string | null;
  driveLogosRootFolderId: string | null;
  emailFromAddress: string | null;
  mondaySpeakerBoardId: string | null;
  mondaySalesBoardId: string | null;
  slackWebhookUrl: string | null;
  slackChannelName: string | null;
  /** Whether a per-event Personatech API key is stored (encrypted). The key
   *  itself is write-only and never returned. */
  hasPersonatechApiToken: boolean;
  /** Whether the per-event Personatech Participant LIST API key is stored
   *  (encrypted). Separate key — Personatech issues one key per endpoint. */
  hasPersonatechParticipantListApiToken: boolean;
  /** Whether the per-event Personatech Participant SEARCH API key is stored
   *  (encrypted). Separate key — Personatech issues one key per endpoint. */
  hasPersonatechParticipantSearchApiToken: boolean;
  integrationConfig: Record<string, unknown> & {
    /** Per-event module toggles. Absent on existing events = all modules on. */
    modules?: EventModules;
  };
  emailDryRun: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Mirror of the Python-only Brand model. The credential-isolation boundary
 *  across events. Secrets are write-only (entered in the dashboard, stored
 *  encrypted); responses expose only `has*` booleans. Any unset secret falls
 *  back to the brand's `{VAR}__{SLUG}` env var, then the unsuffixed global. */
export interface Brand {
  id: string;
  name: string;
  /** Lowercase [a-z0-9_] env-var suffix, e.g. "astraeus". */
  slug: string;
  /** HubSpot portal this brand owns — the inbound-webhook routing key. */
  hubspotPortalId: string | null;
  /** Non-secret Personatech base URL. */
  personatechApiBaseUrl: string | null;
  /** Non-secret Google Workspace delegated subject (email); null = use env. */
  googleDelegatedSubject: string | null;
  isActive: boolean;
  /** Whether each secret is stored (encrypted). The value is never returned. */
  hasHubspotAccessToken: boolean;
  hasHubspotClientSecret: boolean;
  hasMondayApiToken: boolean;
  hasPersonatechApiToken: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Single integration's health-check shape (src/integrations/factory.ts). */
export interface HealthResult {
  ok: boolean;
  latencyMs: number;
  message?: string;
  details?: unknown;
}

/** GET /api/events/:id/health — keyed by integration name. */
export type HealthAggregate = Record<string, HealthResult>;

/**
 * One row of GET /api/speakers (server: src/pixel/api/speakers.py
 * SpeakerRosterOut). The monday board is the source of truth. Onboarding is ONE
 * `speaker_onboarding` workflow per speaker, so the row summarizes whether it
 * has started, its live workflow status, and whether any gate is open — every
 * row opens the speaker's journey.
 */
export interface SpeakerRosterEntry {
  itemId: string;
  eventId: string;
  name: string;
  company: string | null;
  jobTitle: string | null;
  email: string | null;
  category: string | null;
  previousSpeaker: boolean;
  phase: string | null;
  mondayStatus: string | null;
  assetsIn: number;
  assetsTotal: number;
  /** Whether THE onboarding workflow exists for this speaker. */
  started: boolean;
  /** The onboarding workflow's id, or null before onboarding starts. */
  workflowId: string | null;
  /** The onboarding workflow's live status (pending/running/paused/…), or null. */
  workflowStatus: string | null;
  gatesOpen: number;
  attention: boolean;
  /** Why the row needs (or doesn't need) attention — 'calm' = quietly on
   *  track, null = nothing derivable. Drives the roster AttnChip. */
  attentionKind: 'gate' | 'stale' | 'assets' | 'failed' | 'calm' | null;
  /** Human sentence for the chip, e.g. "Gate open · Confirmation". */
  attentionText: string | null;
  /** The next journey step's display name ("Start onboarding" pre-start;
   *  null once everything is done). */
  nextStepName: string | null;
  /** When that next step happens, e.g. "Waiting for reply" / "Opens Aug 25". */
  nextWhenText: string | null;
  /** Done journey nodes (numerator for the roster progress glyph). */
  doneCount: number;
  /** Which onboarding attempt this row summarizes (re-invites bump it). */
  attempt: number;
  /** Nudges already sent across the row's band-A threads. */
  nudgeSentCount: number;
  /** Earliest still-planned nudge due time (naive-UTC ISO), or null. */
  nudgeNextDueAt: string | null;
  /** A planned nudge hit a send failure (retried next tick). */
  nudgeError: boolean;
  /**
   * The entry point the live monday row justifies when a (re-)start is
   * possible ('invite' | 'invite_reply' | 'confirmation' | 'asset_collection'
   * | 'tail'); null once started (and not re-invitable) or when declined.
   */
  recommendedStartAt: string | null;
  /**
   * Batch eligibility for the roster's "all eligible" actions: the speaker's
   * illustration can be generated now (card gate armed, not yet generated), and
   * the logistics gate can be released for review now (date-armed/held).
   */
  canGenerateCard: boolean;
  canReviewLogistics: boolean;
  /**
   * Send-the-card (promotion) gate can be opened for review now: armed AND the
   * finished card is on the monday row (the precondition is the card existing,
   * not a manual release).
   */
  canPromote: boolean;
  /** The remaining band-B email gates as bulk gate-release actions (prep-call
   * notes + post-event thank-you), so every gate is bulkable. */
  canReviewPrepNotes: boolean;
  canReviewThankYou: boolean;
  /**
   * Human-readable "why not" for an INELIGIBLE speaker — the batch drawer lists
   * who was left out and the reason (design §8). Null when the action IS
   * possible (mirrors the matching can* flag).
   */
  cardSkipReason: string | null;
  logisticsSkipReason: string | null;
  promoteSkipReason: string | null;
  prepNotesSkipReason: string | null;
  thankYouSkipReason: string | null;
  /**
   * Per-action status columns (replaces the single "next action" cell): one
   * display cell per batch action, keyed cards | promotion | prep_notes |
   * logistics | thank_you. `state` picks the chip treatment; `label` is the
   * short status; `reason` is an optional tooltip qualifier.
   */
  actions: Record<string, ActionCell>;
}

/** One roster per-action status cell (the batch-action status columns). */
export interface ActionCell {
  state: 'na' | 'upcoming' | 'ready' | 'review' | 'done' | 'blocked' | 'failed';
  label: string;
  reason: string | null;
}

/**
 * Trigger taxonomy metadata for one journey step (server: TriggerOut). The
 * trigger is the first-class concept on the speaker side — each step of the
 * onboarding workflow fires on a DIFFERENT one. `automatic` is false ONLY for
 * the person-started `manual` trigger; every other trigger is system-fired.
 *
 * NOTE on `icon`: it is a backend HINT key and is NOT guaranteed to be a valid
 * client `IconName` (e.g. "clock"/"events"). Resolve the actual icon through the
 * typed client map (`lib/speakerTriggers.ts`) keyed by `key` — never pass
 * `trigger.icon` straight to <Icon>.
 */
export interface SpeakerTriggerMeta {
  /** manual|reply|asset|quorum|schedule|before|dayof|after */
  key: string;
  /** Full label, e.g. "Before event". */
  label: string;
  /** Chip label, e.g. "T-minus". */
  short: string;
  /** Backend icon hint — DO NOT pass straight to <Icon>; resolve via the client map. */
  icon: string;
  /** One-line semantics. */
  blurb: string;
  /** false ONLY for "manual" (person-started); true = the system fires it. */
  automatic: boolean;
}

/**
 * One tracked asset on a speaker's journey (server: TrackedAssetOut).
 * `state` is the asset's collection state: a fresh receipt, already on file from
 * a prior event, manually handled off-platform, or still outstanding.
 */
export interface SpeakerTrackedAsset {
  /** headshot|bio|reg|hotel|marketing */
  key: string;
  label: string;
  note: string;
  state: 'received' | 'on_file' | 'manual' | 'outstanding';
  /** Satisfied by inbound-email evidence rather than the board (headshot only
   *  — monday's file column can't display it; the file lives in Drive). Shows
   *  the "Via email" mail mark. */
  viaEmail: boolean;
}

/**
 * One step's live state on a speaker's journey (server: JourneyNodeOut).
 * The whole journey belongs to ONE onboarding workflow (SpeakerJourney.workflowId)
 * — nodes address their step via `stepIndex`. `band` 0 is the sequential head
 * (invite → reply → confirmation → assets); `band` 1 is the armed tail whose
 * steps fire on their own triggers in any order. `kind` is 'email' (a human
 * gate) or 'wait' (a parked, Trigger-now-able step). `status` / `whenText` /
 * `blockedReason` are server-derived for display; `gateOpen` (a drafted email
 * awaits approval) and `canRunNow` (manual resume / early release — or, before
 * any workflow exists, the kick-off affordance on the invite node) drive the
 * actions. `skipped` marks a done step resolved without sending.
 */
export interface SpeakerJourneyNode {
  id: string;
  name: string;
  kind: 'email' | 'wait' | 'task';
  /** 0 = the sequential head; 1 = the armed tail. */
  band: number;
  /** The step's index in THE onboarding workflow (gate links + trigger-now). */
  stepIndex: number;
  /** The trigger taxonomy object (was a bare string pre-rebuild). */
  trigger: SpeakerTriggerMeta;
  summary: string;
  firesTag: string | null;
  /** Server-derived display status. */
  status: 'done' | 'active' | 'blocked' | 'scheduled';
  /** e.g. "Opens Aug 25 · T-3 weeks", "Waiting for the speaker's reply". */
  whenText: string;
  blockedReason: string | null;
  /** A drafted email awaits approval → "Review & approve". */
  gateOpen: boolean;
  /** Manual override: resume/release the step now (kick-off when no workflow yet). */
  canRunNow: boolean;
  done: boolean;
  /** Done without sending (e.g. marked sent outside Pixel). */
  skipped: boolean;
  /** When a band-1 date-armed step opens (naive-UTC ISO, `Z`-suffixed), else null. */
  scheduledFor: string | null;
  /** The hold event this step is parked on (e.g. 'promotion_released') while
   *  currently held — null once released or for non-holdFor waits. Drives the
   *  "Release now" affordance. */
  heldBy: string | null;
  /** A date-armed step holding with NO date (event date unknown at arming, or
   *  a reviewer cleared it) — offer the date input. */
  needsDate: boolean;
  /** A band-1 gate parked with no arming condition, so a mid-way start queues
   *  no approvals. Nothing is waiting on the world — it opens when someone
   *  asks, via the same release call as a held gate. */
  awaitingRequest: boolean;
  /** Seeded by a mid-way start — this step happened OUTSIDE Pixel, so there is
   *  no activity trail for it. Rendered greyed out, with no "what happened". */
  cutover: boolean;
  /** Shelved in the workflow definition — Pixel never runs this step (the prep
   *  call and its notes email are handled by the team). Rendered as a compact
   *  greyed note: no status pill, no wait reason, no controls. Unlike
   *  `cutover` (this RUN happened by hand), this means Pixel never does it. */
  handledOutside: boolean;
}

/**
 * The nudge plan attached to one band-A sent email (server: NudgeSummaryOut).
 * One per send step (invite / confirmation), newest TrackedEmail per step.
 * `status` is the tracked email's own status — a CONFIRMED/IN_CONVERSATION
 * thread explains why its planned nudges were auto-cancelled.
 */
export interface NudgeSummary {
  trackedEmailId: string;
  stepId: string;
  subject: string;
  status: TrackedEmailStatus;
  plan: NudgePlanEntry[];
}

/**
 * One onboarding attempt for a speaker (server: AttemptOut). Attempt 1 is the
 * original invite; a re-invite starts a FRESH workflow with the next ordinal
 * (`triggerSource` suffixed `:rN`). Newest first in `SpeakerJourney.attempts`,
 * current attempt included.
 */
export interface SpeakerAttempt {
  ordinal: number;
  workflowId: string;
  status: string;
  createdAt: string;
}

/**
 * GET /api/speakers/:eventId/:itemId (server: SpeakerJourneyOut) — one speaker's
 * monday record + THE onboarding workflow (one per speaker) + every step's
 * current state on the journey spine.
 */
export interface SpeakerJourney {
  itemId: string;
  eventId: string;
  name: string;
  company: string | null;
  jobTitle: string | null;
  email: string | null;
  category: string | null;
  previousSpeaker: boolean;
  /**
   * Provenance when the returning flag was AUTO-detected at start time
   * (Personatech cross-year search): which past editions they spoke at.
   * Null when the flag came from the manual monday checkbox (or is off).
   */
  previousSpeakerAuto: { source: string; editions: string[] } | null;
  phase: string | null;
  mondayStatus: string | null;
  ticketType: string | null;
  /** THE onboarding workflow's id, or null before onboarding starts. */
  workflowId: string | null;
  /** The onboarding workflow's live status, or null before it exists. */
  workflowStatus: string | null;
  /** The tracked assets (was a Record<string, boolean> dict pre-rebuild). */
  assets: SpeakerTrackedAsset[];
  assetsIn: number;
  assetsTotal: number;
  /**
   * The five IMAGE deliverables read off the monday row's file columns:
   * headshot → illustrated V1 → illustrated final → PT headshot → speaker card.
   * Same shape as `assets`, opposite direction — these are what Pixel PRODUCES,
   * not what we chase the speaker for. They come from the same columns the
   * illustration pipeline checks before generating, so a "received" row here is
   * one that generation will skip.
   */
  deliverables: SpeakerTrackedAsset[];
  deliverablesIn: number;
  deliverablesTotal: number;
  journey: SpeakerJourneyNode[];
  /** Follow-up nudge plans on the band-A sent emails (invite / confirmation). */
  nudges: NudgeSummary[];
  /** Which onboarding attempt this journey shows (1 = original invite). */
  attempt: number;
  /** The current workflow is failed/cancelled → a fresh re-invite is allowed. */
  reinvitable: boolean;
  /** Every attempt for this speaker, newest first (current included). */
  attempts: SpeakerAttempt[];
  /** Server-recommended entry point when a (re-)start is possible, else null. */
  recommendedStartAt: string | null;
  /** Event display context for the hub hero (+ countdown from startDate). */
  eventName: string;
  eventCode: string;
  eventStartDate: string | null;
  eventEndDate: string | null;
  /** Illustration-generation marker (server: workflow.context.cardGeneration,
   *  manifest stripped). `running` = imagegen in flight (auto headshot kick-off
   *  or gate build); `ok` = candidates ready (pre-generated or gate open).
   *  Null when generation never ran. */
  cardGeneration: {
    status: 'running' | 'ok' | 'no_headshot' | 'error';
    startedAt?: string;
    finishedAt?: string | null;
    source?: string;
    candidateCount?: number;
    error?: string;
  } | null;
}

/**
 * GET /api/tracked-emails/:id/nudges/:index/preview (server: preview_nudge) —
 * a strictly read-only, live-computed render of ONE planned nudge exactly as
 * the scheduler would fire it right now. Nothing is claimed, cancelled, or
 * audited. `toInherited`/`ccInherited` mean the entry has no override and the
 * recipients shown were resolved from the original email at preview time —
 * they'd be re-resolved again at actual send time.
 */
export interface NudgePreview {
  /** Reply-threading subject: "Re: {original subject}". */
  subject: string;
  /** Rendered plain-text body — null when nothing is outstanding (asset
   *  template with all assets in; the fire path would auto-cancel). */
  bodyText: string | null;
  to: string[];
  toInherited: boolean;
  cc: string[];
  ccInherited: boolean;
  /** 'speaker_invite' | 'speaker_asset' (the composer used). */
  template: string | null;
  /** Asset template only: every asset is in, so this nudge would be
   *  auto-cancelled instead of sent. */
  nothingOutstanding: boolean;
  /** Asset template only: the assets this nudge would chase. */
  outstanding: SpeakerTrackedAsset[] | null;
  /** Asset template only: assets already received/on file (quiet reassurance). */
  satisfied: SpeakerTrackedAsset[] | null;
  /** Inbound-activity evidence: someone we're chasing wrote to the sender
   *  since we last did. The fire path would hold this nudge; sending needs
   *  the explicit "Send anyway" override. Null = mailbox is clean (or the
   *  check is skipped in dry-run mode). */
  inboundActivity: {
    from: string;
    at: string;
    inThread: boolean;
    subject: string;
  } | null;
  /** The inbound-activity check could not run (Gmail read failed / sender
   *  connection unavailable) — the fire path would HOLD rather than send
   *  unverified. Null = check ran fine (or dry-run). */
  inboundActivityError: string | null;
}

/**
 * Response shape for GET /api/me.
 *
 * Phase 6.5 UI-14 extension: the payload includes the per-user Gmail OAuth
 * connection projection so the Settings card + Sidebar banner can read from
 * a single TanStack Query cache entry (queryKey ['me']).
 *
 * SECURITY: the client MUST NEVER receive `gmailOAuthRefreshTokenCiphertext`
 * or `gmailOAuthScopes` — those are server-only and never serialized to the
 * wire (T-6.5-02-ciphertext-on-wire). The backend projects the User row to
 * the shape below and drops the rest.
 */
export interface Me {
  id: string;
  email: string;
  name: string;
  role: Role;
  /**
   * Server's APP_ENV (development | staging | production | test). Used by the
   * dashboard to relax UI affordances on non-prod deploys — e.g., show the
   * Delete-workflow button to non-admins on staging. The real permission
   * check lives on the server (src/api/workflows.ts DELETE handler); this
   * field just lets the UI avoid showing a button that would 403.
   */
  deployEnv: 'development' | 'staging' | 'production' | 'test';
  /**
   * Feature-flag bootstrap (docs/FEATURE-FLAGS.md). `flags` is the per-user map
   * the server evaluated via the 3-tier precedence (env override -> PostHog ->
   * APP_ENV default); the UI gates off THIS (consistent with the backend's
   * `require_flag` 404 boundary, never a client-only guess). `posthog` is the
   * RUNTIME config for posthog-js — delivered here, NEVER baked into the Vite
   * build (a promoted staging artifact would otherwise carry the staging key
   * into prod); null when PostHog is unconfigured (then `flags` is still valid).
   */
  flags: Record<string, boolean>;
  posthog: { key: string; host: string } | null;
  /**
   * Phase 6.5 UI-14 — legacy single-connection keys, now DERIVED server-side
   * from the connection list: `gmailConnected` = any healthy connection;
   * `gmailLastErrorReason` surfaces only when NO healthy connection remains
   * (drives the Sidebar banner). New consumers should read `gmailConnections`.
   */
  gmailConnected: boolean;
  gmailConnectedAt: string | null;
  gmailLastErrorReason: GmailErrorReason | null;
  /**
   * Multi-account (2026-06): every GmailConnection the user has, ordered by
   * connectedAt ASC — index 0 is the implicit sending fallback when no
   * per-event default applies. `lastErrorReason` is non-null only for
   * tombstoned connections (revoked token — needs Reconnect).
   */
  gmailConnections: GmailConnectionInfo[];
  /** Per-event default sending connection: { [eventId]: gmailConnectionId }. */
  gmailEventDefaults: Record<string, string>;
}

/**
 * Why a Gmail connection was tombstoned. The two real failures need different
 * remedies — `invalid_grant` means Google rejected the refresh token itself
 * (revoked, or a consent screen still in "Testing"), `auth_after_refresh` means
 * a fresh token was still refused, i.e. the grant is missing a scope. `'auth'`
 * is legacy (rows written before the split). Treat ANY value as "needs
 * reconnecting"; the string only chooses the wording.
 */
export type GmailErrorReason =
  | 'invalid_grant'
  | 'auth_after_refresh'
  | 'auth'
  | 'rate_limit'
  | 'transient'
  | 'config';

/**
 * One row of Me.gmailConnections (GET /api/me projection of GmailConnection).
 * SECURITY: never carries the refresh-token ciphertext or scope list.
 */
export interface GmailConnectionInfo {
  id: string;
  /** The ACTUAL authorized mailbox (resolved via users.getProfile), lowercased. */
  email: string;
  connectedAt: string;
  /** Non-null ⇔ tombstoned (can't send until reconnected). See GmailErrorReason. */
  lastErrorReason: string | null;
  /**
   * False ⇔ this connection's grant predates the `gmail.settings.basic` scope,
   * so its sends go out WITHOUT the sender's Gmail signature until reconnected.
   * Derived server-side from the granted scopes (the raw scope list is never
   * exposed).
   */
  canReadSignature: boolean;
}

/**
 * Response shape for GET /api/stats?eventId=X.
 * Prisma `groupBy` returns `_count: { id: number }` — keep the underscore
 * key verbatim so callers can reduce without a transform.
 */
export interface Stats {
  workflowCounts: Array<{ status: WorkflowStatus; _count: { id: number } }>;
  pendingByRole: Array<{ gateRole: Role | null; _count: { id: number } }>;
  overdueCount: number;
}

/** Lightweight user metadata returned by /api/admin/users + /api/admin/access. */
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: Role;
  isActive: boolean;
  /**
   * Phase 6.2 SPN-17: Slack workspace member ID (e.g., 'U02ABCXYZ12').
   * Used by the 48h follow-up scheduler to mention the original approver in
   * the Slack reminder. Settable only by admins via
   * PATCH /api/admin/users/:id/slack-user-id. Null when unset.
   */
  slackUserId: string | null;
}

/** Per-event metadata included in the admin access matrix payload. */
export interface AdminAccessEvent {
  id: string;
  code: string;
  name: string;
  startDate: string;
  isActive: boolean;
}

/** Single coarse grant — presence of a UserEventAccess row (user can access the whole event). */
export interface AdminEventGrant {
  userId: string;
  eventId: string;
}

/** Response shape for GET /api/admin/access. */
export interface AdminAccessMatrix {
  users: AdminUser[];
  events: AdminAccessEvent[];
  eventGrants: AdminEventGrant[];
}

/** One message in the ops-gate Gmail thread viewer (GET /api/workflows/:id/ops-email-thread). */
export interface OpsEmailThreadMessage {
  id: string;
  fromEmail: string;
  fromName: string;
  to: string;
  subject: string;
  /** ISO 8601 (UTC) timestamp the message was received/sent. */
  date: string;
  snippet: string;
  bodyHtml: string | null;
  bodyText: string;
  /** True when the message was sent from the mailbox we're reading (our side). */
  fromMailbox: boolean;
}

/**
 * Result of fetching the welcome→ops conversation. When `available` is false,
 * `reason` explains why (not_connected | no_welcome | message_not_found |
 * read_failed) so the gate shows a friendly empty state instead of an error.
 */
export interface OpsEmailThread {
  available: boolean;
  reason: string | null;
  mailbox: string | null;
  messages: OpsEmailThreadMessage[];
}
